import { useSearchParams } from 'react-router-dom';
import { LayoutGrid, List as ListIcon } from 'lucide-react';
import { cn } from '../../lib/cn';

export type LayoutMode = 'list' | 'tile';

export function parseLayoutMode(value: string | null): LayoutMode {
  return value === 'tile' ? 'tile' : 'list';
}

/**
 * List / Tile switcher for the Campaigns & Posts hub.
 *
 * Rendered by the PAGE, not by the views, so it shares a row with the view tabs (tabs left,
 * layout right). The two tabs deliberately keep SEPARATE url params — `clayout` for Grouped by
 * Campaign, `layout` for All Posts — so choosing Tile in one doesn't silently change the other;
 * that's why the param is a prop rather than being hardcoded here.
 */
export function LayoutToggle({ param }: { param: 'layout' | 'clayout' }) {
  const [params, setParams] = useSearchParams();
  const layout = parseLayoutMode(params.get(param));

  const setLayout = (next: LayoutMode) => {
    const p = new URLSearchParams(params);
    p.set(param, next);
    setParams(p, { replace: true });
  };

  return (
    <div className="flex shrink-0 items-center gap-1 rounded-lg border border-line-input bg-card p-0.5">
      <LayoutToggleButton
        active={layout === 'list'}
        onClick={() => setLayout('list')}
        icon={<ListIcon className="h-4 w-4" aria-hidden />}
        label="List"
      />
      <LayoutToggleButton
        active={layout === 'tile'}
        onClick={() => setLayout('tile')}
        icon={<LayoutGrid className="h-4 w-4" aria-hidden />}
        label="Tile"
      />
    </div>
  );
}

function LayoutToggleButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'focus-ring flex items-center gap-1.5 rounded-md px-3 py-1 text-sm font-medium',
        active ? 'bg-tint text-tint-fg' : 'text-ink-muted hover:text-ink',
      )}
      aria-pressed={active}
    >
      {icon}
      {label}
    </button>
  );
}
